import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, type OnInit } from '@angular/core';
import { combineLatest, type Observable, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { FitTextDirective } from '../../../directives/fit-text/fit-text.directive';
import type { BusinessIntelligenceMeasureCode, BusinessIntelligenceMemberCode } from '../../../interfaces/BusinessIntelligence.interface';
import type { BusinessIntelligenceRepresentationComponentConfig } from '../../../interfaces/BusinessIntelligenceRepresentation.interface';
import { LoadingAndErrorComponent } from '../../loading-and-error/loading-and-error';
import { BusinessIntelligenceRepresentationComponent } from '../business-intelligence-representation-component.class';

export interface BusinessIntelligenceSingleValueComponentConfig extends BusinessIntelligenceRepresentationComponentConfig {
    measureCode: BusinessIntelligenceMeasureCode;
    caption: string;
}
@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./business-intelligence-single-value.scss'],
    selector: 'business-intelligence-single-value',
    templateUrl: 'business-intelligence-single-value.html',
    imports: [FitTextDirective, LoadingAndErrorComponent, AsyncPipe]
})
export class BusinessIntelligenceSingleValueComponent
    extends BusinessIntelligenceRepresentationComponent<BusinessIntelligenceSingleValueComponentConfig>
    implements OnInit
{
    singleValue$!: Observable<string>;
    caption$!: Observable<string>;

    ngOnInit() {
        this.singleValue$ = combineLatest([this._representationData$, this._config$]).pipe(
            filter(
                ([representationData, config]) =>
                    representationData && !!Object.keys(representationData.metadata).length && isNotNullish(config)
            ),
            map(([representationData, config]) => {
                return {
                    config,
                    metadata: representationData.metadata,
                    meaningfulData: representationData.data.filter(d => isNotNullish(d.measures[config.measureCode]))
                };
            }),
            switchMap(({ meaningfulData, metadata, config }) => {
                if (!meaningfulData.length) {
                    return of('');
                }
                return metadata[config.measureCode].valuesFormatting.formatAsStandaloneText$(
                    meaningfulData[0].measures[config.measureCode]
                );
            })
        );
        this.caption$ = this._config$.pipe(map(config => (config ? config.caption : '')));
    }

    getExportMembers(config: BusinessIntelligenceSingleValueComponentConfig): BusinessIntelligenceMemberCode[] {
        return [config.measureCode];
    }
}
